import { ReactNode } from 'react'
import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faEllipsisH } from '@fortawesome/free-solid-svg-icons'
import {
  useDeleteRun,
  useDownloadRun,
  usePublishRun,
  useResendRun,
  useRestoreRun,
  useUnpublishRun,
} from '../../../hooks/admin/useFlowBilling'
import { formatInTimezone } from '../../../utils/formatInTimezone'
import type { FlowBillingRun } from '../../../types/flowBilling'

/**
 * What can be done to one stored run, shared by the run history and the
 * month-by-month preview so the two can never offer different things.
 *
 * Delete never destroys anything. The server marks the run deleted and keeps
 * its files; Restore brings it back as a working copy.
 */

/**
 * Runs an administrator may delete: their own working copies.
 *
 * Deliberately excludes sent, published and superseded. Those are what the
 * district received or can still open. The server enforces the same list;
 * this only avoids offering a button that would be refused.
 */
const DELETABLE: string[] = ['generated', 'failed', 'claimed']

/**
 * Runs the group can currently see, and which an admin may therefore take back.
 *
 * 'superseded' is absent: it is what the district was shown before a
 * correction, nothing restores it, and the server refuses to touch it.
 */
const WITHDRAWABLE: string[] = ['sent', 'published']

export function StatusPill({ status }: { status: FlowBillingRun['status'] }) {
  const styles: Record<string, string> = {
    sent: 'bg-green-100 text-green-800',
    published: 'bg-green-100 text-green-800',
    generated: 'bg-blue-100 text-blue-800',
    claimed: 'bg-gray-100 text-gray-600',
    failed: 'bg-red-100 text-red-800',
    superseded: 'bg-gray-100 text-gray-500',
    deleted: 'bg-gray-100 text-gray-500 line-through',
  }
  return (
    <span className={`inline-flex rounded px-2 py-0.5 text-xs ${styles[status] || styles.claimed}`}>
      {status}
    </span>
  )
}

/**
 * Overflow menu for a run's less-used actions.
 *
 * CSV and PDF stay on the row — they are what an administrator clicks most and
 * they are never destructive. Everything that publishes, recalls or deletes
 * goes in here, out of reach of a mis-aimed click on "CSV".
 */
function RowActionsMenu({ children }: { children: ReactNode }) {
  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <button
          type="button"
          aria-label="More actions"
          className="ml-3 px-2 py-0.5 text-gray-500 rounded hover:bg-gray-100 hover:text-gray-700 data-[state=open]:bg-gray-100"
        >
          <FontAwesomeIcon icon={faEllipsisH} className="h-3.5 w-3.5" />
        </button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="end"
          sideOffset={4}
          className="z-50 min-w-[11rem] rounded-md border border-gray-200 bg-white py-1 shadow-lg"
        >
          {children}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  )
}

function MenuAction({
  onSelect,
  disabled,
  title,
  tone = 'default',
  children,
}: {
  onSelect: () => void
  disabled?: boolean
  title?: string
  tone?: 'default' | 'warn' | 'danger'
  children: ReactNode
}) {
  const tones = {
    default: 'text-gray-700',
    warn: 'text-amber-700',
    danger: 'text-red-600',
  }
  return (
    <DropdownMenu.Item
      disabled={disabled}
      title={title}
      onSelect={onSelect}
      className={`cursor-pointer px-3 py-1.5 text-sm outline-none data-[highlighted]:bg-gray-50 data-[disabled]:text-gray-400 data-[disabled]:cursor-default ${tones[tone]}`}
    >
      {children}
    </DropdownMenu.Item>
  )
}

export const periodOf = (run: FlowBillingRun) =>
  formatInTimezone(run.periodStart, run.timezone).slice(0, 7)

const KEPT = 'It is marked as deleted, not destroyed — its files are kept and it can be restored.'

/**
 * What the district will see afterwards, said before anything happens.
 *
 * The page cannot know whether an earlier version exists without asking the
 * server, so the warning covers both outcomes rather than promising one.
 */
function withdrawalWarning(run: FlowBillingRun, action: string): string {
  const deletes = action.includes('delete') ? ` ${KEPT}` : ''
  return (
    `${action} the report for ${periodOf(run)}?\n\n` +
    'The group will see the previous version of this period again, or nothing at all if this ' +
    `was its only report.${deletes}`
  )
}

export function RunActions({
  run,
  onToggleError,
  errorShown = false,
}: {
  run: FlowBillingRun
  /** Offered only where the caller has somewhere to show the error. */
  onToggleError?: () => void
  errorShown?: boolean
}) {
  const download = useDownloadRun()
  const resend = useResendRun()
  const publish = usePublishRun()
  const remove = useDeleteRun()
  const unpublish = useUnpublishRun()
  const restore = useRestoreRun()

  return (
    <>
      <button
        type="button"
        className="text-blue-600 hover:underline disabled:text-gray-400"
        disabled={download.isPending}
        onClick={() => download.mutate({ runId: run.id, format: 'csv' })}
      >
        CSV
      </button>
      <button
        type="button"
        className="ml-3 text-blue-600 hover:underline disabled:text-gray-400"
        disabled={download.isPending}
        onClick={() => download.mutate({ runId: run.id, format: 'pdf' })}
      >
        PDF
      </button>
      <RowActionsMenu>
        {run.status === 'deleted' && (
          <MenuAction
            disabled={restore.isPending}
            onSelect={() => restore.mutate(run.id)}
            title="Bring this run back as a working copy. It is not republished."
          >
            Restore
          </MenuAction>
        )}
        {/* Publishing is the only way a run reaches the group without an
            email, so it is offered on the run itself. */}
        {run.status === 'generated' && (
          <MenuAction
            tone="warn"
            disabled={publish.isPending}
            onSelect={() => publish.mutate(run.id)}
            title="Make this the report the group sees for this period. Emails nobody."
          >
            Publish
          </MenuAction>
        )}
        {run.status !== 'claimed' && run.status !== 'deleted' && (
          <MenuAction
            disabled={resend.isPending}
            onSelect={() => resend.mutate(run.id)}
            title="Re-send the stored file to the current recipient list. Does not recompute."
          >
            Re-send
          </MenuAction>
        )}
        {DELETABLE.includes(run.status) && (
          <MenuAction
            tone="danger"
            disabled={remove.isPending}
            onSelect={() => {
              if (
                window.confirm(
                  `Delete the ${run.status} run for ${periodOf(run)}? Nothing outside the admin panel has seen it.\n\n${KEPT}`
                )
              ) {
                remove.mutate({ runId: run.id })
              }
            }}
            title="Mark this run as deleted. It is kept and can be restored."
          >
            Delete
          </MenuAction>
        )}
        {WITHDRAWABLE.includes(run.status) && (
          <>
            <MenuAction
              tone="warn"
              disabled={unpublish.isPending}
              onSelect={() => {
                if (window.confirm(withdrawalWarning(run, 'Withdraw'))) unpublish.mutate(run.id)
              }}
              title="Take this report back from the group. It stays here as a working copy."
            >
              Unpublish
            </MenuAction>
            {/* Two acts in one, because stopping between them leaves a period
                showing nothing with no record of why. */}
            <MenuAction
              tone="danger"
              disabled={remove.isPending}
              onSelect={() => {
                if (window.confirm(withdrawalWarning(run, 'Withdraw and delete'))) {
                  remove.mutate({ runId: run.id, withdraw: true })
                }
              }}
              title="Take this report back from the group and mark it deleted"
            >
              Withdraw &amp; delete
            </MenuAction>
          </>
        )}
        {run.error && onToggleError && (
          <MenuAction tone="danger" onSelect={onToggleError}>
            {errorShown ? 'Hide error' : 'Show error'}
          </MenuAction>
        )}
      </RowActionsMenu>
    </>
  )
}
